import { createContext, useContext, useEffect, ReactNode } from "react";
import { useLocation } from "wouter";
import { useGetMe, useLogout, useRegister, storeSessionId } from "@workspace/api-client-react";
import type { UserWithStore } from "@workspace/api-client-react/src/generated/api.schemas";
import { useQueryClient } from "@tanstack/react-query";
import { getGetMeQueryKey } from "@workspace/api-client-react";
import { setStoreTimezone } from "@/lib/store-timezone";

interface AuthContextType {
  user: UserWithStore | null;
  isLoading: boolean;
  register: ReturnType<typeof useRegister>["mutateAsync"];
  logout: ReturnType<typeof useLogout>["mutateAsync"];
  isRegistering: boolean;
  isLoggingOut: boolean;
  loginWithSession: (data: { user: any; store: any; sessionId: string }) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();

  // Plano e recursos da loja valem na aba aberta (Pedro, 24/10/2026: a loja que sai do Ultra seguia lendo código de
  // barras até recarregar): ao voltar à janela o /me é buscado de novo, no máximo a cada 30 s, e a cada 5 min com a aba
  // à vista (o caixa do PDV fica o dia todo na mesma janela). Se falhar, fica o que já estava (o React Query mantém os
  // dados).
  const { data: user, isLoading, error } = useGetMe({
    query: {
      retry: false,
      refetchOnWindowFocus: true,
      staleTime: 30_000,
      refetchInterval: 5 * 60_000,
    }
  });

  // Fuso da loja alimenta os helpers de data/promoção do painel admin.
  useEffect(() => {
    setStoreTimezone((user as any)?.store?.timezone ?? null);
  }, [(user as any)?.store?.timezone]);

  const registerMutation = useRegister({
    mutation: {
      onSuccess: (data: any) => {
        if (data?.sessionId) storeSessionId(data.sessionId);
        queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
      }
    }
  });

  const logoutMutation = useLogout({
    mutation: {
      onSuccess: () => {
        storeSessionId(null);
        queryClient.setQueryData(getGetMeQueryKey(), null);
        setLocation("/login");
      }
    }
  });

  const loginWithSession = (data: { user: any; store: any; sessionId: string }) => {
    if (data.sessionId) storeSessionId(data.sessionId);
    // Seed otimista pra render instantâneo, mas o verify-magic-link NÃO retorna
    // `units` — sem o invalidate o switcher fica sem as filiais até um reload
    // manual (matriz aparece via store.name, mas o dropdown vem vazio).
    queryClient.setQueryData(getGetMeQueryKey(), { user: data.user, store: data.store });
    queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
    setLocation("/");
  };

  return (
    <AuthContext.Provider
      value={{
        user: user || null,
        isLoading,
        register: registerMutation.mutateAsync,
        logout: logoutMutation.mutateAsync,
        isRegistering: registerMutation.isPending,
        isLoggingOut: logoutMutation.isPending,
        loginWithSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
